/**
 * +quests: how each side-quest NPC looks (content zones/mossvaleQuestNpcs.ts): one of the KayKit
 * adventurer kits with a tint, a scale, the gear nodes left visible, an idle clip and a prop of its
 * own next to it (outdoor/questProps.ts). Read by outdoor/npcs.ts (figureFor).
 */
import './questProps';

export interface QuestNpcLook {
  file: 'Knight' | 'Mage' | 'Barbarian' | 'Rogue_Hooded';
  gear: string[];
  tint: number;
  scale: number;
  prop?: { id: string; lx: number; lz: number; yaw?: number };
  /** Idle clip (default 'Idle'): the kits have Idle, 2H_Melee_Idle, Blocking and Spellcasting. */
  clip?: string;
  /** A soft glow at chest height (the ghost). */
  glow?: readonly [number, number, number];
}

export const QUEST_NPC_LOOKS: Readonly<Record<string, QuestNpcLook>> = {
  // The Slime Hollows
  qn_hobb: { file: 'Barbarian', gear: [], tint: 0xd8c49c, scale: 1.0, prop: { id: 'q_laundry', lx: 1.4, lz: -1.2, yaw: 0.2 } },
  qn_pell: { file: 'Mage', gear: [], tint: 0xf2d4e8, scale: 0.88, prop: { id: 'q_yarn_basket', lx: 1.1, lz: 0.4 } },
  qn_pip: { file: 'Mage', gear: ['Spellbook_open'], tint: 0xc8f0a8, scale: 0.68, clip: 'Spellcasting', prop: { id: 'q_slime_jar', lx: 1.0, lz: 0.2, yaw: -0.4 } },
  // Brookside Farm and the Boar Run
  qn_stuffins: { file: 'Rogue_Hooded', gear: [], tint: 0xf0d070, scale: 1.06, prop: { id: 'q_empty_post', lx: -1.6, lz: -0.8 } },
  qn_bessa: { file: 'Rogue_Hooded', gear: [], tint: 0xffc4b4, scale: 0.94, prop: { id: 'q_turnips', lx: 0.4, lz: -1.6, yaw: 0.1 } },
  // Barrowfield
  qn_moll: { file: 'Barbarian', gear: [], tint: 0xa8aeb8, scale: 0.98, prop: { id: 'q_spade_mound', lx: 1.5, lz: -0.6, yaw: 0.5 } },
  qn_fennick: { file: 'Mage', gear: ['2H_Staff'], tint: 0xf6f6f2, scale: 1.0, prop: { id: 'q_lantern_pole', lx: -1.2, lz: 0.2 } },
  // Glimmermere and the Lakeside Outpost
  qn_wendel: { file: 'Rogue_Hooded', gear: [], tint: 0x8fb8dc, scale: 1.0, prop: { id: 'q_fishing_spot', lx: 0.6, lz: 0.4 } },
  qn_tilly: { file: 'Barbarian', gear: ['Mug'], tint: 0xfff0d4, scale: 0.92, prop: { id: 'q_cookpot', lx: 1.3, lz: 0.6 } },
  qn_percy: { file: 'Knight', gear: ['Spellbook'], tint: 0xf0c8ff, scale: 0.95, prop: { id: 'q_suitcase', lx: 1.0, lz: -0.3, yaw: 0.3 } },
  // Greyhorn Heights and Tallowdeep
  qn_hesk: { file: 'Barbarian', gear: ['1H_Axe'], tint: 0xc8a070, scale: 1.02, prop: { id: 'q_ore_cart', lx: -1.8, lz: -0.6, yaw: 0.4 } },
  qn_ottilie: { file: 'Rogue_Hooded', gear: ['2H_Staff'], tint: 0xd8f0c0, scale: 0.95, prop: { id: 'q_goat', lx: 1.5, lz: 0.4, yaw: -0.6 } },
  qn_wick: { file: 'Mage', gear: [], tint: 0xfff0a0, scale: 0.96, prop: { id: 'q_candles', lx: 1.2, lz: 0.1 } },
  // Bramblewood
  qn_kip: { file: 'Rogue_Hooded', gear: ['Knife'], tint: 0xc05a50, scale: 0.9, clip: '2H_Melee_Idle', prop: { id: 'q_bandit_sign', lx: -1.3, lz: -0.4, yaw: 0.2 } },
  qn_barnaby: { file: 'Barbarian', gear: [], tint: 0xb0784a, scale: 1.08, prop: { id: 'q_handle_stump', lx: 1.3, lz: -0.3 } },
  // The Pale Rise and the Tower Road
  qn_waverly: { file: 'Knight', gear: ['1H_Sword'], tint: 0xb4d4ff, scale: 1.0, glow: [0.35, 0.55, 0.9], prop: { id: 'q_lantern_pole', lx: 1.1, lz: 0.3 } },
  qn_agatha: { file: 'Mage', gear: ['Spellbook_open'], tint: 0xead8a8, scale: 0.97, clip: 'Spellcasting', prop: { id: 'q_dig_site', lx: 1.2, lz: -0.9 } },
  qn_bartram: { file: 'Mage', gear: ['Spellbook'], tint: 0xc8cce8, scale: 0.98, prop: { id: 'q_clerk_desk', lx: 0, lz: 1.1, yaw: Math.PI } },
};
